<template>
  <div class="service-form">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">{{ isEdit ? `Edit service: ${form.id}` : "Create service" }}</h4>
      <router-link :to="{ name: 'offer-services-list' }" class="btn btn-default btn-sm">Back to list</router-link>
    </div>

    <div class="service-form__layout">
      <!-- Main: translations -->
      <div class="service-form__main">
        <card title="Translations" class="service-form__card">
          <el-tabs v-model="activeTab" type="card">
            <el-tab-pane v-for="l in langs" :key="l" :name="l">
              <template slot="label">
                <span>
                  {{ l.toUpperCase() }}
                  <span v-if="form.name[l]" class="dot-filled" />
                </span>
              </template>
              <div class="service-form__editor-wrap">
                <label>Name ({{ l.toUpperCase() }})</label>
                <el-input
                  v-model="form.name[l]"
                  type="textarea"
                  :autosize="{ minRows: 2, maxRows: 6 }"
                  :dir="l === 'ar' ? 'rtl' : 'ltr'"
                  :placeholder="`Name in ${l.toUpperCase()}`"
                />
              </div>
            </el-tab-pane>
          </el-tabs>
        </card>
      </div>

      <!-- Sidebar -->
      <div class="service-form__sidebar">
        <card title="Settings" class="service-form__card">
          <label>Type *</label>
          <el-select v-model="form.type" placeholder="Select type" style="width: 100%" class="mb-3">
            <el-option v-for="t in types" :key="t.value" :label="t.label" :value="t.value" />
          </el-select>

          <label>ID (slug)</label>
          <el-input
            v-model="form.id"
            :disabled="isEdit"
            :placeholder="isEdit ? '' : 'Auto-generated from EN name'"
          />
          <small v-if="!isEdit" class="text-muted d-block mt-1">
            Leave blank — the server will generate a slug from the English name.
          </small>
        </card>

        <div class="mt-3">
          <el-button type="primary" :loading="saving" @click="save" style="width: 100%">
            {{ isEdit ? "Save Changes" : "Create" }}
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import APIOfferServices from "@/api/modules/offer-services";

const TYPES = [
  { label: "Medical", value: "medical" },
  { label: "Common", value: "common" },
  { label: "Implant", value: "implant" },
  { label: "Transfer", value: "transfer" },
  { label: "Accommodation", value: "accommodation" },
];

const emptyName = (langs = []) => langs.reduce((acc, l) => ({ ...acc, [l]: "" }), {});

export default {
  props: {
    id: { type: String, default: "" },
  },
  data() {
    return {
      types: TYPES,
      activeTab: "en",
      saving: false,
      form: {
        id: "",
        type: "medical",
        name: {},
      },
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.id);
    },
    langs() {
      return (this.$store.state.cmsApiCatalogs.domains || [])
        .map(({ language }) => language)
        .filter(Boolean);
    },
  },
  methods: {
    ...mapActions("offerServices", ["upsertService"]),
    ...mapActions("cmsApiCatalogs", ["getDomains"]),
    initLangFields() {
      this.form.name = { ...emptyName(this.langs), ...(this.form.name || {}) };
      if (this.langs.length && !this.langs.includes(this.activeTab)) {
        this.activeTab = this.langs[0];
      }
    },
    async load() {
      if (!this.isEdit) return;
      this.$store.dispatch("setLoader", true);
      try {
        const response = await APIOfferServices.getServices({});
        const list = (response.data && (response.data.services || response.data)) || [];
        const found = list.find((s) => s.id === this.id);
        if (!found) {
          this.$notify({ type: "warning", title: "Service not found" });
          this.$router.push({ name: "offer-services-list" });
          return;
        }
        this.form.id = found.id;
        this.form.type = found.type || "medical";
        this.form.name = { ...emptyName(this.langs), ...(found.name || {}) };
      } finally {
        this.$store.dispatch("setLoader", false);
      }
    },
    async save() {
      if (!this.form.type) {
        return this.$notify({ type: "warning", title: "Type is required" });
      }
      if (!this.form.name.en || !this.form.name.en.trim()) {
        return this.$notify({ type: "warning", title: "English name is required" });
      }
      this.saving = true;
      try {
        const name = {};
        Object.keys(this.form.name).forEach((l) => {
          const v = (this.form.name[l] || "").trim();
          if (v) name[l] = v;
        });
        const payload = {
          id: this.form.id || "",
          type: this.form.type,
          name,
        };
        await this.upsertService(payload);
        this.$notify({ type: "success", title: this.isEdit ? "Service updated" : "Service created" });
        this.$router.push({ name: "offer-services-list" });
      } catch (e) {
        this.$notify({ type: "warning", title: "Error saving service" });
      } finally {
        this.saving = false;
      }
    },
  },
  async created() {
    await this.getDomains();
    this.initLangFields();
    this.load();
  },
};
</script>

<style scoped>
.service-form__layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.service-form__card {
  border-radius: 12px;
}
.service-form__editor-wrap {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  padding: 12px;
  background: #fcfdff;
}
.service-form__editor-wrap label {
  display: inline-block;
  margin-bottom: 8px;
  font-weight: 600;
}
.service-form__main {
  flex: 1;
  min-width: 0;
}
.service-form__sidebar {
  width: 320px;
  flex-shrink: 0;
}
.dot-filled {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #67c23a;
  margin-left: 4px;
  vertical-align: middle;
}
</style>
